import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
import { VoiceConcierge } from "@/components/VoiceConcierge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChefHat, ShieldCheck, User as UserIcon, LogOut, Menu as MenuIcon, X } from "lucide-react";

function BrandMark() {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="brand-home">
      <img
        src="/chef-hire-logo.png"
        alt="Chef Hire"
        className="h-14 w-14 object-contain"
      />
      <div className="leading-tight">
        <div className="font-script text-2xl -mb-1 text-white">Chef</div>
        <div className="small-caps text-[10px] text-neutral-300">Hire</div>
      </div>
    </Link>
  );
}

export default function Layout({ children }) {
  const { user, logout, setAuthOpen, openAuth } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const goAdmin = () => {
    if (user?.role === "admin" || user?.role === "manager") {
      nav("/admin");
    } else {
      openAuth({ tab: "admin" });
    }
  };

  const nav_items = [
    { to: "/chefs", label: "Private Chefs" },
    { to: "/menus", label: "Mini Catering" },
    { to: "/chefs-table", label: "Chef's Table" },
    { to: "/membership", label: "Black" },
  ];

  const portalLink = user?.role === "admin"
    ? { to: "/admin", label: "Admin" }
    : user?.role === "chef" || user?.role === "manager"
    ? { to: "/chef", label: "Chef Portal" }
    : { to: "/my-bookings", label: "My Bookings" };

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-white/10 backdrop-blur-2xl bg-black/70">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <BrandMark />

          <nav className="hidden md:flex items-center gap-10">
            {nav_items.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                className={`small-caps text-[11px] transition ${loc.pathname.startsWith(n.to) ? "text-white" : "text-neutral-400 hover:text-white"}`}
              >
                {n.label}
              </Link>
            ))}
            {user && (
              <Link
                to={portalLink.to}
                data-testid="nav-portal"
                className="small-caps text-[11px] text-neutral-400 hover:text-white"
              >
                {portalLink.label}
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-3 pl-2 pr-3 py-1.5 border border-white/10 hover:border-white/20" data-testid="user-menu-btn">
                    <Avatar className="h-7 w-7 rounded-none">
                      <AvatarImage src={user.picture || user.avatar_url} />
                      <AvatarFallback className="rounded-none bg-neutral-800 text-xs">
                        {(user.name || "?").slice(0,1).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block text-sm text-neutral-300 max-w-[140px] truncate">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-[#141414] border-white/10 rounded-none" align="end">
                  <DropdownMenuLabel className="text-neutral-500 small-caps text-[10px]">
                    {user.role}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={() => nav(portalLink.to)}
                    data-testid="menu-portal"
                    className="rounded-none focus:bg-white/5"
                  >
                    {portalLink.label === "Admin" ? <ShieldCheck size={14} className="mr-2" /> : <UserIcon size={14} className="mr-2" />}
                    {portalLink.label}
                  </DropdownMenuItem>
                  {user.role !== "chef" && user.role !== "admin" && (
                    <DropdownMenuItem
                      onClick={() => nav("/chef")}
                      data-testid="menu-apply-chef"
                      className="rounded-none focus:bg-white/5"
                    >
                      <ChefHat size={14} className="mr-2" /> Apply as chef
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout" className="rounded-none focus:bg-white/5">
                    <LogOut size={14} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setAuthOpen(true)}
                  data-testid="signin-btn"
                  className="hidden sm:inline-flex text-neutral-300 hover:text-white hover:bg-transparent small-caps text-[11px] rounded-none"
                >
                  Sign in
                </Button>
                <button
                  onClick={() => setAuthOpen(true)}
                  data-testid="get-started-btn"
                  className="btn-primary text-[11px] small-caps"
                >
                  Book a Chef
                </button>
              </>
            )}

            <button
              className="md:hidden text-neutral-300"
              onClick={() => setMobileOpen((s) => !s)}
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X size={20} /> : <MenuIcon size={20} />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-white/10 bg-black">
            <div className="px-6 py-4 flex flex-col gap-3">
              {nav_items.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="small-caps text-neutral-300">
                  {n.label}
                </Link>
              ))}
              {user && (
                <Link to={portalLink.to} onClick={() => setMobileOpen(false)} className="small-caps text-neutral-300">
                  {portalLink.label}
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="min-h-[70vh]">{children}</main>

      <footer className="mt-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <BrandMark />
            <p className="text-neutral-400 mt-6 max-w-md leading-relaxed">
              Discreet, curated private chefs and mini-catering for India's discerning homes.
              Mumbai · Bengaluru · Indore — and on request, wherever you dine.
            </p>
          </div>
          <div>
            <div className="small-caps text-neutral-500 mb-4">Services</div>
            <ul className="space-y-2 text-neutral-300 text-sm">
              <li><Link to="/chefs" className="hover:text-white transition">Private Chefs</Link></li>
              <li><Link to="/private-chef-mumbai" className="hover:text-white transition">Private Chef Mumbai</Link></li>
              <li><Link to="/private-chef-bengaluru" className="hover:text-white transition">Private Chef Bengaluru</Link></li>
              <li><Link to="/private-chef-indore" className="hover:text-white transition">Private Chef Indore</Link></li>
              <li><Link to="/private-chef-delhi-ncr" className="hover:text-white transition">Private Chef Delhi NCR</Link></li>
              <li><Link to="/private-chef-hyderabad" className="hover:text-white transition">Private Chef Hyderabad</Link></li>
              <li><Link to="/menus" className="hover:text-white transition">Mini Catering</Link></li>
              <li><Link to="/chefs-table" className="hover:text-white transition">Chef's Table · Indore</Link></li>
              <li><Link to="/diwali" className="hover:text-[#F5B041] transition">Diwali Gourmet Sit-down</Link></li>
              <li><button onClick={() => openAuth()} data-testid="footer-chef-apply" className="text-neutral-300 hover:text-white transition text-left">Apply as chef</button></li>
            </ul>
          </div>
          <div>
            <div className="small-caps text-neutral-500 mb-4">Team</div>
            <ul className="space-y-2 text-neutral-300 text-sm">
              <li>
                <button
                  onClick={goAdmin}
                  data-testid="footer-admin-access"
                  className="text-neutral-300 hover:text-[#F5B041] transition"
                >
                  Admin access →
                </button>
              </li>
              <li><a href="tel:+917971413816" className="hover:text-[#F5B041] transition">Voice concierge · +91 79714 13816</a></li>
              <li><a href="tel:+916352553515" className="hover:text-white transition">+91 63525 53515</a></li>
              <li><a href="mailto:hello@chefhire.in" className="hover:text-white transition">hello@chefhire.in</a></li>
              <li><Link to="/terms" data-testid="footer-terms" className="hover:text-[#F5B041] transition">Terms &amp; Conditions</Link></li>
              <li className="text-neutral-500 text-xs">One month's salary + GST · Staff on our payroll</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 px-6 flex flex-wrap items-center justify-between gap-3 text-neutral-500">
          <div className="small-caps text-[10px]">© {new Date().getFullYear()} Chef Hire · Crafted for HNI dining</div>
          <button
            onClick={goAdmin}
            data-testid="footer-admin-mini"
            className="small-caps text-[10px] text-neutral-600 hover:text-[#F5B041] transition"
          >
            Admin
          </button>
        </div>
      </footer>

      <AuthModal />
      <VoiceConcierge />
    </>
  );
}
